import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 80px;
  display: flex;
  align-items: center;
  gap: 20px;
  
  @media (max-width: 768px) {
    margin-bottom: 60px;
    gap: 10px;
  }
`;

const CarouselContent = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;
  
  -webkit-mask-image: linear-gradient(
    to right,
    transparent 0%,
    black 8%,
    black 92%,
    transparent 100%
  );
  mask-image: linear-gradient(
    to right,
    transparent 0%,
    black 8%,
    black 92%,
    transparent 100%
  );
`;

const CarouselWrapper = styled.div`
  display: flex;
  transition: transform 0.5s ease;
  will-change: transform;
`;

const CompanyCard = styled(motion.div)`
  background: rgba(135,206,235,0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(135,206,235,0.3);
  padding: 25px 20px;
  border-radius: 15px;
  text-align: center;
  height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  cursor: pointer;
  will-change: transform, opacity;
  flex-shrink: 0;
  margin-right: 20px;
  
  /* Desktop: 5 cards per row */
  @media (min-width: 769px) {
    width: calc((100% - 80px) / 5);
    
    &:hover {
      transform: translateY(-5px);
      border-color: rgba(135,206,235,0.5);
    }
  }
  
  /* Mobile: 3 cards per row */
  @media (max-width: 768px) {
    width: calc((100% - 40px) / 3);
    margin-right: 10px;
  }
`;

const NavigationButton = styled.button`
  background: rgba(135,206,235,0.15);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(135,206,235,0.2);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #87ceeb;
  flex-shrink: 0;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  
  &:hover {
    background: rgba(135,206,235,0.25);
    border-color: rgba(135,206,235,0.4);
    backdrop-filter: blur(16px);
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
  }
`;

const TrustedCompaniesCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const intervalRef = useRef(null);
  
  const companies = [
    { name: 'TechCorp' },
    { name: 'InnovateLab' },
    { name: 'DigitalFlow' },
    { name: 'StartupHub' },
    { name: 'CloudTech' },
    { name: 'DataSoft' },
    { name: 'WebSolutions' },
    { name: 'AppMakers' }
  ];

  // Create infinite loop by duplicating cards
  const extendedCompanies = [...companies, ...companies, ...companies];
  const startIndex = companies.length; // Start from middle set

  // Calculate items per view based on screen size
  const getItemsPerView = () => {
    if (window.innerWidth <= 768) return 3; // Mobile
    return 5; // Desktop
  };

  const [itemsPerView, setItemsPerView] = useState(getItemsPerView());

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setItemsPerView(getItemsPerView());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize currentIndex to startIndex
  useEffect(() => {
    setCurrentIndex(startIndex);
  }, [startIndex]);

  // Auto-slide functionality with infinite loop
  useEffect(() => {
    if (isAutoPlaying) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex(prev => {
          const next = prev + 1;
          // Reset to start of middle set when reaching end
          if (next >= startIndex + companies.length) {
            setTimeout(() => setCurrentIndex(startIndex), 0);
            return startIndex;
          }
          return next;
        });
      }, 3000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAutoPlaying, startIndex, companies.length]);

  const goToPrevious = () => {
    setCurrentIndex(prev => {
      const next = prev - 1;
      // Reset to end of middle set when going before start
      if (next < startIndex) {
        setTimeout(() => setCurrentIndex(startIndex + companies.length - 1), 0);
        return startIndex + companies.length - 1;
      }
      return next;
    });
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const goToNext = () => {
    setCurrentIndex(prev => {
      const next = prev + 1;
      // Reset to start of middle set when reaching end
      if (next >= startIndex + companies.length) {
        setTimeout(() => setCurrentIndex(startIndex), 0);
        return startIndex;
      }
      return next;
    });
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  return (
    <CarouselContainer>
      <NavigationButton onClick={goToPrevious}>
        <ChevronLeft size={20} />
      </NavigationButton>
      
      <CarouselContent>
        <CarouselWrapper
          style={{
            transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)`,
          }}
        >
          {extendedCompanies.map((company, index) => (
            <CompanyCard
              key={index}
              initial={{ opacity: 0, y: 3 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.12, 
                ease: "easeOut", 
                delay: (index % itemsPerView) * 0.02 
              }}
            >
              <h4 style={{ 
                color: '#ffffff', 
                marginBottom: '0px', 
                fontSize: window.innerWidth <= 768 ? '0.8rem' : '1.1rem', 
                fontWeight: '600' 
              }}>
                {company.name}
              </h4>
            </CompanyCard>
          ))}
        </CarouselWrapper>
      </CarouselContent>
      
      <NavigationButton onClick={goToNext}>
        <ChevronRight size={20} />
      </NavigationButton>
    </CarouselContainer>
  );
};

export default TrustedCompaniesCarousel;